import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Video, Clock, X, FileText, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { meetingAPI } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { parseUTC, fmtInTz, fmtWhen } from '@/components/meetings/shared';

// Persist which meetings we've reminded / popped (per local day) so each fires
// once, not on every poll.
const REMIND_KEY = 'meeting.reminded';
const MODAL_KEY = 'meeting.modalShown';
const GRACE_MS = 5 * 60000; // show the start modal up to 5 min after start

function loadSet(key) {
  try {
    const raw = JSON.parse(localStorage.getItem(key) || '{}');
    return raw.day === new Date().toDateString() ? new Set(raw.ids || []) : new Set();
  } catch { return new Set(); }
}
function saveSet(key, set) {
  try { localStorage.setItem(key, JSON.stringify({ day: new Date().toDateString(), ids: [...set] })); } catch { /* ignore */ }
}

/**
 * App-wide meeting reminders:
 *  - a pre-meeting toast at the user's lead time (title/time/organizer + Join), and
 *  - a blocking modal at start time so an overlooked toast can't make you miss it.
 */
export default function MeetingReminderWatcher() {
  const navigate = useNavigate();
  const remindedRef = useRef(loadSet(REMIND_KEY));
  const modalShownRef = useRef(loadSet(MODAL_KEY));
  const [startModal, setStartModal] = useState(null);

  const { data: prefs } = useQuery({ queryKey: ['meeting-prefs'], queryFn: () => meetingAPI.prefs(), staleTime: 60000 });
  const reminderMin = prefs?.reminder_minutes ?? 10;

  const { data: meetings = [] } = useQuery({
    queryKey: ['reminder-upcoming'],
    queryFn: () => meetingAPI.list({ scope: 'upcoming' }),
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const now = Date.now();
    const windowMs = (reminderMin || 0) * 60000;
    for (const m of meetings) {
      if (m.status === 'cancelled') continue;
      const start = parseUTC(m.start_at)?.getTime();
      if (!start) continue;
      const lead = start - now;

      // Pre-meeting toast.
      if (reminderMin && lead > 0 && lead <= windowMs && !remindedRef.current.has(m.id)) {
        remindedRef.current.add(m.id);
        saveSet(REMIND_KEY, remindedRef.current);
        fireToast(m, navigate);
      }

      // Start-time modal (fires once, at/just-after start).
      if (lead <= 0 && lead > -GRACE_MS && !modalShownRef.current.has(m.id)) {
        modalShownRef.current.add(m.id);
        saveSet(MODAL_KEY, modalShownRef.current);
        setStartModal(m);
      }
    }
  }, [meetings, reminderMin, navigate]);

  return (
    <Dialog open={!!startModal} onOpenChange={(v) => { if (!v) setStartModal(null); }}>
      <DialogContent className="sm:max-w-md">
        {startModal && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-primary">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10"><Clock size={16} /></span>
                Your meeting is starting
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-2.5">
              <p className="text-lg font-semibold">{startModal.title}</p>
              <Row icon={Clock}>{fmtWhen(startModal)} · {startModal.duration_minutes} min</Row>
              {startModal.organizer_name && <Row icon={Users}>Organized by {startModal.organizer_name}</Row>}
              {startModal.agenda && <Row icon={FileText}><span className="whitespace-pre-wrap">{startModal.agenda}</span></Row>}
              <div className="flex gap-2 pt-2">
                {startModal.google_meet_url ? (
                  <a href={startModal.google_meet_url} target="_blank" rel="noreferrer" className="flex-1" onClick={() => setStartModal(null)}>
                    <Button className="w-full bg-emerald-600 hover:bg-emerald-700"><Video size={16} className="mr-2" /> Join Now</Button>
                  </a>
                ) : (
                  <Button className="flex-1" onClick={() => { const id = startModal.id; setStartModal(null); navigate(`/meetings?m=${id}`); }}>View meeting</Button>
                )}
                <Button variant="outline" onClick={() => setStartModal(null)}><X size={15} className="mr-1" /> Dismiss</Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ icon: Icon, children }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon size={15} className="mt-0.5 shrink-0 text-muted-foreground" />
      <span className="flex-1">{children}</span>
    </div>
  );
}

function fireToast(m, navigate) {
  const when = fmtInTz(parseUTC(m.start_at), m.timezone || 'UTC', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  toast.custom((t) => (
    <div className={`pointer-events-auto w-80 rounded-xl border border-border/60 bg-card p-3 shadow-lg ${t.visible ? 'animate-in fade-in slide-in-from-top-2' : 'opacity-0'}`}>
      <div className="flex items-start gap-2">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Clock size={16} /></span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">Meeting reminder</p>
          <p className="truncate text-sm font-medium">{m.title}</p>
          <p className="text-xs text-muted-foreground">{when} · {m.organizer_name || 'Organizer'}</p>
        </div>
        <button onClick={() => toast.dismiss(t.id)} className="text-muted-foreground hover:text-foreground"><X size={14} /></button>
      </div>
      <div className="mt-2 flex gap-2">
        {m.google_meet_url ? (
          <a href={m.google_meet_url} target="_blank" rel="noreferrer" onClick={() => toast.dismiss(t.id)} className="flex-1">
            <button className="flex w-full items-center justify-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700">
              <Video size={14} /> Join Meeting
            </button>
          </a>
        ) : (
          <button onClick={() => { toast.dismiss(t.id); navigate(`/meetings?m=${m.id}`); }}
            className="flex-1 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90">
            View meeting
          </button>
        )}
      </div>
    </div>
  ), { duration: 30000, position: 'top-right' });
}
